import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';

export type CategoryScope =
  | { kind: 'own' }
  | { kind: 'family-shared' }
  | { kind: 'member'; clientId: string };

export type FamilyMember = { clientId: string; displayName: string };

/**
 * A FilesTable-compatible row for synthetic category entries.
 *
 * `isFolder: true` ensures the row renders as a folder and suppresses
 * FiledDocumentInlineActions (which returns null for folders).
 */
export type CategoryRow = UploadedFile & { categoryScope: CategoryScope };

const SYNTHETIC_ID_PREFIX = 'category';

function makeSyntheticId(kind: string, qualifier?: string): string {
  return qualifier
    ? `${SYNTHETIC_ID_PREFIX}:${kind}:${qualifier}`
    : `${SYNTHETIC_ID_PREFIX}:${kind}`;
}

function makeCategoryRow(displayName: string, scope: CategoryScope): CategoryRow {
  const syntheticId =
    scope.kind === 'member'
      ? makeSyntheticId('member', scope.clientId)
      : makeSyntheticId(scope.kind);

  return {
    id: syntheticId,
    isFolder: true,
    isResolved: true,
    createdAt: '',
    detectedClients: [],
    file: {
      id: -1,
      fileName: displayName,
      isFolder: true,
      isResolved: true,
      isFiled: true,
      isTemporary: false,
      createdAt: '',
      fileSize: 0,
      fileType: '',
    },
    categoryScope: scope,
  };
}

/**
 * Builds root records for the family filed documents view.
 *
 * Family-owned files have `clientId === undefined`; member-owned files have `clientId` set.
 * Do NOT pass this array as `allOtherFiles` to useFiledDocumentsView — it mixes real
 * UploadedFiles with synthetic CategoryRows and TypeScript will not catch the misuse.
 */
export function buildFamilyRootRecords(
  files: UploadedFile[],
  orderedMembers: FamilyMember[],
): (UploadedFile | CategoryRow)[] {
  const familyRootRecords = files.filter(f => !f.clientId && f.parentId == null);

  const memberCategoryRows = orderedMembers.map(member =>
    makeCategoryRow(member.displayName, { kind: 'member', clientId: member.clientId }),
  );

  return [...familyRootRecords, ...memberCategoryRows];
}

/**
 * Builds root records for the individual client filed documents view.
 *
 * Do NOT pass this array as `allOtherFiles` to useFiledDocumentsView — it mixes real
 * UploadedFiles with synthetic CategoryRows and TypeScript will not catch the misuse.
 */
export function buildClientRootRecords(
  files: UploadedFile[],
  hasFamily: boolean,
): (UploadedFile | CategoryRow)[] {
  const clientRootRecords = files.filter(f => f.parentId == null);

  if (!hasFamily) {
    return clientRootRecords;
  }

  const familySharedRow = makeCategoryRow('Family (shared)', { kind: 'family-shared' });

  return [...clientRootRecords, familySharedRow];
}

/**
 * Filters files by scope.
 *
 * Discriminates on `clientId`: family-owned files have `clientId === undefined`;
 * member-owned files have `clientId` set to that member's id.
 */
export function filterFilesByScope(files: UploadedFile[], scope: CategoryScope): UploadedFile[] {
  if (scope.kind === 'family-shared') {
    return files.filter(f => !f.clientId);
  }

  if (scope.kind === 'member') {
    return files.filter(f => f.clientId === scope.clientId);
  }

  return files;
}
